import { ClipboardCheck, LineChart, Send } from "lucide-react";

import {
  ORIENTATION_CONTINUE,
  ORIENTATION_JOBS,
  ORIENTATION_TITLE,
} from "../../lib/portal-copy.js";

import type { CSSProperties, ReactNode } from "react";

/**
 * Post-welcome orientation — three equal-weight jobs before the Home hub.
 * Session-gated via `t33OrientationSeen` (see portal-copy.ts).
 *
 * Transition33 partners get the T33 palette for brand continuity with
 * Transition33Welcome; other partners use default portal tokens.
 */

const T33_PALETTE = {
  "--color-background": "#f8f8f8",
  "--color-fg": "#111b27",
  "--color-fg-muted": "#313d4d",
  "--color-fg-subtle": "#6e7b8b",
  "--color-brand": "#003b44",
  "--color-accent": "#ca8a04",
  "--color-border": "#dde2e8",
} as CSSProperties;

const JOB_ICONS: Record<(typeof ORIENTATION_JOBS)[number]["id"], ReactNode> = {
  attend: <ClipboardCheck aria-hidden="true" className="size-6" />,
  programme: <LineChart aria-hidden="true" className="size-6" />,
  send: <Send aria-hidden="true" className="size-6" />,
};

export function PortalOrientation({
  isTransition33,
  onContinue,
}: {
  isTransition33: boolean;
  onContinue: () => void;
}) {
  const paletteStyle = isTransition33 ? T33_PALETTE : undefined;

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center overflow-auto bg-surface-muted p-6"
      style={paletteStyle}
    >
      <div
        className="w-full max-w-2xl border bg-surface p-8 shadow-sm"
        style={isTransition33 ? { borderColor: "var(--color-border)" } : undefined}
      >
        <div className="eyebrow">{ORIENTATION_TITLE}</div>
        <h1 className="mt-3 text-3xl font-bold text-fg">{ORIENTATION_TITLE}</h1>
        <p className="mt-2 max-w-xl text-sm text-fg-muted">
          Three things this workspace is for — pick up where you left off anytime from Overview.
        </p>

        <ul className="mt-8 grid gap-4 sm:grid-cols-3">
          {ORIENTATION_JOBS.map((job) => (
            <li
              key={job.id}
              className="flex flex-col gap-3 border bg-surface-muted p-4"
              style={isTransition33 ? { borderColor: "var(--color-border)" } : undefined}
            >
              <span
                className="flex size-10 items-center justify-center rounded-md bg-info-bg text-brand"
                style={isTransition33 ? { color: "var(--color-brand)" } : undefined}
              >
                {JOB_ICONS[job.id]}
              </span>
              <div>
                <h2 className="text-sm font-semibold text-fg">{job.title}</h2>
                <p className="mt-1 text-sm text-fg-muted">{job.description}</p>
              </div>
            </li>
          ))}
        </ul>

        <div className="mt-8">
          <button
            type="button"
            onClick={onContinue}
            className={
              isTransition33
                ? "inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-semibold focus-visible:outline-2 focus-visible:outline-offset-2"
                : "inline-flex items-center gap-2 rounded-md bg-brand px-5 py-2.5 text-sm font-semibold text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2"
            }
            style={
              isTransition33
                ? {
                    backgroundColor: "var(--color-brand)",
                    color: "var(--color-background)",
                    outlineColor: "var(--color-accent)",
                  }
                : undefined
            }
          >
            {ORIENTATION_CONTINUE}
            {isTransition33 && (
              <span aria-hidden="true" style={{ color: "var(--color-accent)" }}>
                →
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
